/*
 * Copyright (c) 2024. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import {CheckCircleIcon, TrashIcon, XCircleIcon, XMarkIcon} from "@heroicons/react/24/solid";
import {TicketIcon} from "@heroicons/react/24/outline";
import {getValidityByWeekDay, useVoucherSubLabelByPersonLimit, useVoucherTypeByArticle} from "utils/voucherUtils";
import React from "react";
import {VoucherItem} from "types/entities/VoucherItem";
import {useTranslation} from "react-i18next";
import {useTypedSelector} from "helpers/reducers";
import VoucherItemDateValidity from "pages/Front/components/VoucherItemDateValidity";

interface VoucherItemProps {
    voucherItem: VoucherItem;
    foundVoucher?: boolean;
    onClick: () => void;
}

const BookingVoucherItem = (props: VoucherItemProps) => {
    const {t} = useTranslation();
    // const Package = useTypedSelector(state => state.bookingState.bookingProcess.package);
    // const voucherItemEligibleForPackageCart = Package !== null ? getEligiblePackageFromVoucherItem(props.voucherItem, Package) : false;
    const filter = useTypedSelector(state => state.bookingState.bookingProcess.filter);
    const getValidityByWeekDayObject = getValidityByWeekDay(props.voucherItem, filter.date);
    const DELETE_ENABLED = props.voucherItem.campaign_name !== "FELIXTAG";
    const voucherSubLabelByPersonLimit = useVoucherSubLabelByPersonLimit();
    const {getVoucherType} = useVoucherTypeByArticle();

    return (
        <article
            key={props.voucherItem.id}
            className={"BookingVoucherItem relative flex flex-col items-center justify-center w-full"}
            data-active={props.foundVoucher}
            data-voucher-id={props.voucherItem.id}
            title={props.foundVoucher ? t("Active voucher") : t("Inactive voucher")}
        >
            <div className={"flex flex-row relative items-stretch shadow rounded-md overflow-hidden justify-between w-full"}>
                <div className={"flex justify-center items-center text-contrast-800 bg-arbo-orange p-2"}>
                    {props.voucherItem.amount_left === 0 ? <XMarkIcon className={"w-8 h-8"} /> : <TicketIcon className={"w-8 h-8"} />}
                </div>
                <div className={"flex flex-col items-start w-full grow p-2 bg-gray-300 bg-opacity-50"}>
                    <div className={"relative flex flex-row items-center border-b w-full border-gray-700 p-1"}>
                        <dt className="text-base font-normal text-gray-900">{props.voucherItem.code}</dt>
                        <div className={"VoucherApplicableStatus ml-2"}>
                            {props.foundVoucher && props.voucherItem.validity && <CheckCircleIcon className={"w-4 h-4 mr-2 text-green-600"} />}
                            {props.foundVoucher && !props.voucherItem.validity && <XCircleIcon className={"w-4 h-4 mr-2 text-red-600"} />}
                        </div>
                        <VoucherItemDateValidity voucherItemValidity={getValidityByWeekDayObject} />
                    </div>
                    <div className={"flex flex-row items-center justify-between w-full p-1"}>
                        <div className="flex items-baseline text-xl font-semibold text-arbo-orange">
                            {getVoucherType(props.voucherItem).valueFormatted}
                            <span className="ml-2 text-sm font-medium text-gray-800">{getVoucherType(props.voucherItem).label}</span>
                        </div>
                        {DELETE_ENABLED && (
                            <button
                                className={"w-6 h-6 flex items-center justify-center mt-1 ml-2 hover:text-red-700 "}
                                title={t("Delete voucher")}
                                type="button"
                                onClick={props.onClick}
                            >
                                <TrashIcon className={"w-5 h-5 text-inherit"} />
                            </button>
                        )}
                    </div>
                    <div className={"flex flex-row items-center justify-between w-full p-1 text-sm"}>
                        <span>{voucherSubLabelByPersonLimit.getVoucherSubLabel(props.voucherItem)}</span>
                    </div>
                    {(props.voucherItem.amount_left === 0 || !props.voucherItem.validity) && (
                        <div className={"flex flex-row items-center justify-between w-full p-1"}>
                            <span className={"text-sm font-medium text-red-700"}>{t("Voucher invalid")}</span>
                        </div>
                    )}
                </div>
            </div>
        </article>
    );
};

export default BookingVoucherItem;
